import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';

interface CreateRoleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (roleData: any) => void;
  availablePermissions: Array<{ id: string; name: string; resource: string; action: string }>;
  permissionGroups: Array<{ id: string; name: string; permissions: string[] }>;
}

const CreateRoleDialog: React.FC<CreateRoleDialogProps> = ({
  open,
  onClose,
  onSave,
  availablePermissions,
  permissionGroups,
}) => {
  const [roleName, setRoleName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const handleSave = () => {
    const roleData = {
      name: roleName,
      description,
      permissions: selectedPermissions,
      permissionGroups: selectedGroups,
    };
    onSave(roleData);
    handleClose();
  };

  const handleClose = () => {
    setRoleName('');
    setDescription('');
    setSelectedPermissions([]);
    setSelectedGroups([]);
    onClose();
  };

  const togglePermission = (permissionKey: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionKey)
        ? prev.filter((p) => p !== permissionKey)
        : [...prev, permissionKey]
    );
  };

  const toggleGroup = (groupId: string) => {
    setSelectedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((g) => g !== groupId)
        : [...prev, groupId]
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Create New Role
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            Create a custom role with specific permissions. You can assign individual permissions or use permission groups.
          </Alert>

          <TextField
            fullWidth
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            sx={{ mb: 3 }}
            required
          />

          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Permission Groups
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select pre-configured permission groups to assign to this role
          </Typography>
          <Box sx={{ mb: 3 }}>
            {permissionGroups.map((group) => (
              <FormControlLabel
                key={group.id}
                control={
                  <Checkbox
                    checked={selectedGroups.includes(group.id)}
                    onChange={() => toggleGroup(group.id)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {group.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {group.permissions.length} permissions
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Individual Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select specific permissions to assign to this role
          </Typography>
          <List sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {availablePermissions.map((permission) => {
              const permissionKey = `${permission.resource}:${permission.action}`;
              const isSelected = selectedPermissions.includes(permissionKey);
              return (
                <ListItem
                  key={permission.id}
                  button
                  onClick={() => togglePermission(permissionKey)}
                  sx={{
                    backgroundColor: isSelected ? 'action.selected' : 'transparent',
                  }}
                >
                  <ListItemIcon>
                    <Checkbox checked={isSelected} />
                  </ListItemIcon>
                  <ListItemText
                    primary={permission.name}
                    secondary={
                      <Box display="flex" gap={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                        <Chip label={permission.resource} size="small" variant="outlined" />
                        <Chip label={permission.action} size="small" variant="outlined" />
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>

          {(selectedPermissions.length > 0 || selectedGroups.length > 0) && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                SELECTED ({selectedPermissions.length + selectedGroups.length})
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {selectedGroups.map((groupId) => {
                  const group = permissionGroups.find((g) => g.id === groupId);
                  return (
                    <Chip
                      key={groupId}
                      label={`Group: ${group?.name}`}
                      size="small"
                      color="primary"
                      onDelete={() => toggleGroup(groupId)}
                    />
                  );
                })}
                {selectedPermissions.map((perm) => (
                  <Chip
                    key={perm}
                    label={perm}
                    size="small"
                    variant="outlined"
                    onDelete={() => togglePermission(perm)}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!roleName || (selectedPermissions.length === 0 && selectedGroups.length === 0)}
        >
          Create Role
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateRoleDialog;

